import { Link } from "react-router-dom";
import { ArrowRight, Scale, Receipt, Landmark, Sparkles } from "lucide-react";
import { Nav } from "@/components/Nav";
import { ResultPreview } from "@/components/ResultPreview";

const LEDGER_IMG = "https://images.unsplash.com/photo-1595014361739-7370d0fc74ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwYWNjb3VudGluZyUyMGxlZGdlciUyMGJvb2t8ZW58MHx8fHwxNzg1NjYzNjQ3fDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Scale, title: "Double-entry audit", body: "Deterministic checks on balance, both legs, positive amounts and named ledgers before a single token is spent." },
  { icon: Sparkles, title: "Ind AS ↔ AS switch", body: "The same entry, reviewed twice. See how recognition and measurement shift between Ind AS and Indian GAAP." },
  { icon: Receipt, title: "GST awareness", body: "CGST / SGST / IGST split, input tax credit eligibility and reverse charge flagged in plain English." },
  { icon: Landmark, title: "Companies Act notes", body: "Schedule III presentation and disclosure implications noted alongside every verdict." },
];

export default function Landing() {
  return (
    <div className="grain min-h-screen">
      <Nav />

      <section className="relative overflow-hidden border-b border-stone-200">
        <img src={LEDGER_IMG} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
          <p className="overline animate-rise text-slate-500">For Chartered Accountants · Articles · Finance teams</p>
          <h1 className="animate-rise font-display mt-6 max-w-3xl text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            Unsure about a Journal entry?<br />We'll review it instantly!
          </h1>
        <div className="animate-rise mt-8 max-w-xl space-y-2 text-base leading-relaxed text-slate-600" style={{ animationDelay: "160ms" }}>
            <p>Put in your debit and credit lines.</p>
            <p>We tell you if the entry is right or wrong.</p>
            <p>You also get the corrected entry, the standard behind it, and the GST and Companies Act impact.</p>
          </div>
          <div className="animate-rise mt-12 flex flex-wrap items-center gap-4" style={{ animationDelay: "240ms" }}>
            <Link to="/analyze" data-testid="hero-cta" className="group inline-flex items-center gap-2 bg-[#0f172a] px-8 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800">
              Analyze an entry
              <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" />
            </Link>
            <span className="font-mono-data text-xs text-slate-500">No sign-in needed · 50 free reviews</span>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-stone-100/60 py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight md:text-lg">
            A verdict card for every entry — this is exactly how results look.
          </h2>
          <div className="mt-14">
            <ResultPreview />
          </div>
          <div className="mt-14 flex flex-wrap items-center gap-4">
            <Link to="/analyze" data-testid="preview-cta"
              className="group inline-flex items-center gap-2 bg-[#0f172a] px-8 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800">
              Try it now
              <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" />
            </Link>
            <span className="font-mono-data text-xs text-slate-500">50 free reviews without signing in</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12">
        <div className="grid gap-px border border-stone-200 bg-stone-200 md:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="group bg-white p-10 transition-colors duration-150 hover:bg-stone-50">
              <f.icon className="h-5 w-5 text-[#0f172a]" />
              <h2 className="font-display mt-6 text-lg font-bold tracking-tight md:text-lg">{f.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-stone-200 bg-[#0f172a] py-20 text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <p className="overline text-slate-400">Sample</p>
          <h2 className="font-display mt-4 text-2xl font-bold tracking-tight">Dr Machinery · Cr Bank</h2>
          <table className="mt-8 w-full max-w-2xl border-collapse font-mono-data text-sm">
            <tbody className="text-slate-200">
              <tr className="border-b border-slate-700"><td className="w-10 py-3">Dr</td><td className="py-3">Machinery A/c</td><td className="py-3 text-right">₹5,00,000.00</td></tr>
              <tr className="border-b border-slate-700"><td className="py-3">Dr</td><td className="py-3">Input CGST A/c</td><td className="py-3 text-right">₹45,000.00</td></tr>
              <tr className="border-b border-slate-700"><td className="py-3">Dr</td><td className="py-3">Input SGST A/c</td><td className="py-3 text-right">₹45,000.00</td></tr>
              <tr><td className="py-3">Cr</td><td className="py-3 pl-6">Bank A/c</td><td className="py-3 text-right">₹5,90,000.00</td></tr>
            </tbody>
          </table>
          <p className="mt-8 max-w-xl text-sm leading-relaxed text-slate-400">
            Balanced, but is the GST creditable? Is installation cost capitalised under Ind AS 16? That's the part we explain.
          </p>
        </div>
      </section>

      <footer className="border-t border-stone-200 py-10">
        <div className="mx-auto max-w-[1400px] px-6 font-mono-data text-xs text-slate-500 lg:px-12">
          LedgerCheck · Educational review tool. Not a substitute for professional judgement.
        </div>
      </footer>
    </div>
  );
}
